import { SKILL_BUILDER_PROFILE_URL } from '@/src/constants/common';
import { RoutePath } from '@/src/RoutePath';
import { useAuth } from '@/src/store/auth.context';
import { useUser } from '@/src/store/user.context';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { preProdLogger } from '@/src/utils/log.utils';
import { Button, ButtonDropdown, ButtonDropdownProps } from '@amzn/awsui-components-react';
import { CancelableEventHandler } from '@amzn/awsui-components-react/polaris/internal/events';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styles from './UserButton.module.scss';

export const UserButton: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { authClient } = useAuth();
  const history = useHistory();

  const userOptions = useMemo(() => {
    if (!user) return undefined;

    const menuItems: { id: string; text: string; href?: string; external?: boolean }[] = [
      {
        id: 'user-profile',
        text: `${t(i18nKeys.JamProfile.btnHeader)}`,
        href: RoutePath.JAM_PROFILE_DETAILS.valueOf(),
      },
      {
        id: 'skillbuilder-profile',
        text: `${t(i18nKeys.JamProfile.sbProfile)}`,
        href: SKILL_BUILDER_PROFILE_URL,
        external: true,
      },
      { id: 'user-signout', text: `${t(i18nKeys.account.signOut.button)}` },
    ];

    return {
      name: user.nickname || user.name,
      email: user.email,
      menuItems,
    };
  }, [user]);

  const handleSignInClick = useCallback(() => {
    void authClient.signIn(history.location);
  }, [authClient]);

  const handleUserMenuItemClick: CancelableEventHandler<ButtonDropdownProps.ItemClickDetails> = useCallback(
    (e) => {
      const id = e.detail.id;
      switch (id) {
        case 'user-signout':
          authClient
            .signOut()
            .then(() => preProdLogger('sign out'))
            .catch((err) => preProdLogger(err));
          break;
      }
    },
    [authClient]
  );

  const loggedInMenu = useMemo((): ButtonDropdownProps.Item[] => {
    if (!userOptions) return [];
    return [{ id: 'user-email', text: userOptions.email }, ...userOptions.menuItems];
  }, [userOptions]);

  if (!userOptions) {
    return (
      <div className={styles.button}>
        <Button variant="primary" data-testid="signInButton" onClick={handleSignInClick}>
          {t(i18nKeys.account.login.button)}
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.button}>
      <ButtonDropdown items={loggedInMenu} data-testid="user-menu-btn" onItemClick={handleUserMenuItemClick}>
        <FontAwesomeIcon icon="user" /> {userOptions.name}
      </ButtonDropdown>
    </div>
  );
};
